'use client';

import { supabase } from '@/services';
import { User } from '@supabase/supabase-js';
import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { STORAGE_KEY } from '../constants';
import { useLocalStorage } from '../hooks';
import { datadogRum } from '@datadog/browser-rum';
import appLogger from '@/utils/logger';

// Event name must match useLocalStorage hook
const LOCAL_STORAGE_UPDATE_EVENT = 'mb_local_storage_update';

// Helper to set localStorage AND notify useLocalStorage hook
const setAccessTokenToStorage = (token: string) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY.ACCESS_TOKEN, token);
  // Dispatch event so useLocalStorage hook detects the change
  window.dispatchEvent(
    new CustomEvent(LOCAL_STORAGE_UPDATE_EVENT, {
      detail: { key: STORAGE_KEY.ACCESS_TOKEN, value: token },
    })
  );
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<{
  accessToken: string;
  isAuthenticated: boolean;
  isSessionInitializing: boolean;
  user?: User;
  accessTokenState: string;
  setAccessTokenState: React.Dispatch<React.SetStateAction<string>>;
}>({
  accessToken: '',
  isAuthenticated: false,
  isSessionInitializing: true,
  user: undefined,
  accessTokenState: '',
  setAccessTokenState: () => {},
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>();
  // Access token is stored in local storage
  const [accessToken] = useLocalStorage(STORAGE_KEY.ACCESS_TOKEN, null);
  const [ctiAccessToken] = useLocalStorage(STORAGE_KEY.CTI_ACCESS_TOKEN, null);
  const [ctiUserId] = useLocalStorage(STORAGE_KEY.CTI_USER_ID, null);

  // CTI mode uses an ephemeral API token (not a Supabase JWT).
  // We treat it as authenticated for the purpose of calling our private APIs.
  const isCtiMode = !!ctiAccessToken && !accessToken;
  const resolvedAccessToken = accessToken || ctiAccessToken || '';
  const isAuthenticated = !!resolvedAccessToken;
  const [isHydrated, setIsHydrated] = useState(false);

  // Access token state to use for mfa check
  const [accessTokenState, setAccessTokenState] = useState('');

  // Session initialization state - prevents auth checks before session is synced
  const [isSessionInitializing, setIsSessionInitializing] = useState(true);

  // Handle hydration and initialize session
  useEffect(() => {
    setIsHydrated(true);

    // Additional safeguard: Clear reb2b tracking data during auth initialization
    // This prevents any stale tracking data from interfering with post-login navigation
    if (typeof window !== 'undefined' && window.localStorage) {
      const reb2bKeys = ['_reb2bref', '_reb2bsessionID', '_reb2buid'];
      reb2bKeys.forEach((key) => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
    }

    // Set up Supabase auth state change listener
    if (isCtiMode) {
      // CTI mode doesn't need Supabase session - mark initialization complete immediately
      setIsSessionInitializing(false);
      return () => {};
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      appLogger.debug('auth_state_changed', { component: 'AuthProvider', event });
      
      if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        // Update the stored access token when it's refreshed
        setAccessTokenToStorage(session.access_token);
        appLogger.info('auth_token_refreshed', {
          component: 'AuthProvider',
          userId: session.user?.id,
        });
      } else if (event === 'SIGNED_OUT' || (event === 'USER_UPDATED' && !session)) {
        // Clear storage and redirect to login on sign out
        if (typeof window !== 'undefined') {
          localStorage.removeItem(STORAGE_KEY.ACCESS_TOKEN);
          window.location.pathname = '/login';
        }
      }
    });

    // CRITICAL FIX: Initialize session immediately on mount to prevent 401 errors
    // This ensures the token is refreshed and stored before components make API calls
    const initializeSession = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !sessionData?.session) {
          // Try to refresh if session is missing
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

          if (!refreshError && refreshData?.session?.access_token) {
            setAccessTokenToStorage(refreshData.session.access_token);
            appLogger.info('auth_session_initialized_on_mount', {
              component: 'AuthProvider',
              source: 'refresh',
            });
          }
        } else if (sessionData.session?.access_token) {
          // Ensure token is stored even if session exists but localStorage is out of sync
          const currentToken = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY.ACCESS_TOKEN) : null;
          if (currentToken !== sessionData.session.access_token) {
            setAccessTokenToStorage(sessionData.session.access_token);
            appLogger.info('auth_token_synced_on_mount', {
              component: 'AuthProvider',
              source: 'getSession',
            });
          }
        }
      } catch (error) {
        appLogger.warn('auth_session_init_failed', {
          component: 'AuthProvider',
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        // Session initialization complete - allow auth checks to proceed
        setIsSessionInitializing(false);
      }
    };

    // Initialize session immediately (don't wait for accessToken to exist)
    if (!isCtiMode) {
      initializeSession();
    }

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [isCtiMode]);

  useEffect(() => {
    // Only check/refresh session if user previously had a token (was authenticated)
    // This prevents unauthenticated visitors on public pages from being redirected to /login
    if (!isHydrated || isCtiMode || !accessToken) return;
    handleGetUserInfo();
  }, [isHydrated, isCtiMode, accessToken]);

  // In CTI mode, provide a minimal user object so downstream dialer logic has a stable SDR user id.
  useEffect(() => {
    if (!isHydrated) return;
    if (!isCtiMode) return;
    if (!ctiUserId || typeof ctiUserId !== 'string') return;

    setUser({ id: ctiUserId } as unknown as User);
  }, [isHydrated, isCtiMode, ctiUserId]);

  // Set Datadog RUM user context when user changes
  useEffect(() => {
    if (!isHydrated) return;

    try {
      if (user) {
        // User is logged in - set user context
        datadogRum.setUser({
          id: user.id,
          email: user.email || 'unknown',
          name: user.user_metadata?.name || user.email || 'unknown',
        });

        appLogger.info('datadog_user_context_set', {
          component: 'AuthProvider',
          userId: user.id,
          email: user.email,
        });
      } else {
        // User is logged out - clear user context
        datadogRum.clearUser();
        appLogger.info('datadog_user_context_cleared', { component: 'AuthProvider' });
      }
    } catch (error) {
      appLogger.warn('datadog_user_context_failed', {
        component: 'AuthProvider',
        error,
      });
    }
  }, [user, isHydrated]);

  const handleGetUserInfo = async () => {
    try {
      // First, check if we need to refresh the session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session) {
        appLogger.info('auth_session_refresh_attempt', {
          component: 'AuthProvider',
          reason: sessionError ? sessionError.message : 'session_missing',
        });
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData?.session) {
          appLogger.trackError(refreshError, {
            component: 'AuthProvider',
            message: 'Failed to refresh session',
          });
          // Clear local storage and redirect to login
          if (typeof window !== 'undefined') {
            localStorage.removeItem(STORAGE_KEY.ACCESS_TOKEN);
            window.location.pathname = '/login';
          }
          return;
        }
        
        // Update the access token with the refreshed one
        if (refreshData.session?.access_token) {
          setAccessTokenToStorage(refreshData.session.access_token);
        }
      } else if (sessionData.session?.access_token) {
        // CRITICAL FIX: Always sync token to localStorage even if session exists
        // This ensures localStorage is in sync with Supabase session
        const currentToken = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY.ACCESS_TOKEN) : null;
        if (currentToken !== sessionData.session.access_token) {
          setAccessTokenToStorage(sessionData.session.access_token);
          appLogger.info('auth_token_synced', {
            component: 'AuthProvider',
            source: 'handleGetUserInfo',
          });
        }
      }
      
      // Now get the user info
      const {
        data: { user },
      } = await supabase.auth.getUser();

      setUser(user || undefined);
    } catch (error) {
      appLogger.trackError(error, {
        component: 'AuthProvider',
        message: 'Error getting user info',
      });
    }
  };

  // Prevent hydration mismatch by using default values until client-side hydration
  const contextValue = {
    isAuthenticated: isHydrated ? isAuthenticated : false,
    isSessionInitializing: isHydrated ? isSessionInitializing : true,
    accessToken: isHydrated ? resolvedAccessToken : '',
    user: isHydrated ? user : undefined,
    accessTokenState,
    setAccessTokenState,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
